import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, auth } from './firebaseConfig';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { FileText, Briefcase } from 'lucide-react';
import './LinkedInTheme.css';

function CandidateInterviews() {
  const { t, i18n } = useTranslation();
  // Use context for profile data if available in layout
  const contextData = useOutletContext(); 
  const userProfile = contextData ? contextData.userProfile : null;
  
  const [simulations, setSimulations] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default avatar logic (copied from other components for consistency)
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";
  const avatarUrl = userProfile?.avatar_url || defaultAvatar;

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'simulations'), where('participant_uids', 'array-contains', currentUser.uid));
    
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const simsData = [];
      querySnapshot.forEach((doc) => {
        simsData.push({ id: doc.id, ...doc.data() });
      });

      const enrichedSims = await Promise.all(simsData.map(async (sim) => {
        if (sim.jd_id) {
          try {
            const jdRef = doc(db, 'jobDescriptions', sim.jd_id);
            const jdSnap = await getDoc(jdRef);
            if (jdSnap.exists()) {
              return { ...sim, jd_data: jdSnap.data() };
            }
          } catch (e) {
            console.error(`Error fetching JD ${sim.jd_id}:`, e);
          }
        }
        return sim;
      }));

      // Sort by created_at desc
      enrichedSims.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));

      setSimulations(enrichedSims);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching simulations:", err);
      setError("Failed to fetch simulations.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const renderArtifacts = (artifacts) => {
    if (!artifacts) return <p style={{ fontSize: '14px', color: '#666' }}>No assessments available yet.</p>;
    
    const interviewers = ["recruiter", "team-member-1", "team-member-2", "team-member-3", "hiring-manager"];
    
    return (
      <ul style={{ listStyle: 'none', padding: 0, marginTop: '12px' }}>
        {interviewers.map(interviewer => {
          const report = artifacts[interviewer]?.outputs;
          if (!report) return null;

          let reportContent = '';
          if (report.first_interview_report) {
            reportContent = `Status: ${report.first_interview_report.screening_status} - Summary: ${report.first_interview_report.summary}`;
          } else if (report.team_member_notes) {
            reportContent = `Score: ${report.match_score} - Notes: ${report.team_member_notes}`;
          } else if (report.final_decision) {
            reportContent = `Decision: ${report.final_decision.match_status} - Notes: ${report.final_decision.hiring_manager_notes}`;
          }

          const label = interviewer.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

          return (
            <li key={interviewer} style={{ marginBottom: '12px', fontSize: '14px', borderLeft: '2px solid #e0e0e0', paddingLeft: '12px' }}>
              <strong style={{ display: 'block', color: '#000', marginBottom: '4px' }}>{label}:</strong> 
              <span style={{ color: '#666', lineHeight: '1.4' }}>{reportContent || 'No report submitted.'}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="li-layout" style={{ justifyContent: 'center', alignItems: 'center' }}>
         <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="li-layout">
         <div className="li-card li-error-msg" style={{ padding: '20px' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="li-layout">
      <div className="li-container">
         {/* Left Sidebar - Profile Card */}
         <aside className="li-sidebar-left">
            <div className="li-card li-profile-card">
                <div className="li-card-background" style={{ backgroundColor: '#a0b4b7' }}></div>
                <div className="li-card-content">
                    <img 
                        src={avatarUrl} 
                        alt="Profile" 
                        className="li-avatar"
                    />
                    <div className="li-identity">
                        <h3 className="li-name">{userProfile?.firstName} {userProfile?.lastName}</h3>
                         <p className="li-headline">{t('welcome.interviewReportsTitle')}</p>
                    </div>
                </div>
             </div>
         </aside>

         {/* Main Feed - Reports */}
         <main className="li-main-feed">
            <div className="li-card li-feed-post">
                <div className="li-post-header">
                     <div className="li-post-actor-image" style={{ backgroundColor: '#057642', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileText size={24} />
                     </div>
                     <div className="li-post-meta">
                        <span className="li-post-title">{t('myInterviewReports')}</span>
                        <span className="li-post-subtitle">Track your AI Twin's progress</span>
                     </div>
                </div>
            </div>

            {simulations.length === 0 ? (
                 <div className="li-card li-feed-post" style={{ padding: '32px', textAlign: 'center', color: '#666' }}>
                    <p>{t('noInterviewsMessage')}</p>
                    {userProfile?.status !== 'active' && (
                        <Link to="/simulations" className="li-btn-primary" style={{ marginTop: '16px', display: 'inline-block' }}>Activate Simulations</Link>
                    )}
                 </div>
            ) : (
                simulations.map(sim => (
                    <div key={sim.id} className="li-card li-feed-post">
                        <div className="li-post-header">
                             <div className="li-post-actor-image" style={{ backgroundColor: '#f3f2ef', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Briefcase size={24} />
                             </div>
                             <div className="li-post-meta">
                                <span className="li-post-title">{sim.jd_data?.title || t('jobLabel') + ': ' + sim.jd_id}</span>
                                <span className="li-post-time">{sim.created_at?.toDate().toLocaleString(i18n.language)}</span>
                             </div>
                        </div>
                        
                        <div className="li-post-content">
                             {Object.entries(sim.candidates).map(([vtcId, candidateData]) => {
                                // Only render the report if the candidate's userId matches the current user's uid
                                if (candidateData.userId === auth.currentUser.uid) {
                                  const statusKey = `status.${candidateData.status || 'inProgress'}`;
                                  const statusColor = candidateData.status === 'shortlisted' ? '#057642' : candidateData.status === 'rejected' ? '#cc1016' : '#0a66c2';
                                  
                                  return (
                                    <div key={vtcId} style={{ marginTop: '12px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', backgroundColor: '#f9fafb', padding: '10px', borderRadius: '4px' }}>
                                          <span style={{ fontWeight: '600', fontSize: '14px' }}>{t('finalOutcome')}:</span>
                                          <span style={{ fontWeight: 'bold', color: statusColor, fontSize: '14px' }}>{t(statusKey, { defaultValue: candidateData.status })}</span>
                                      </div>
                                      
                                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#0a66c2' }}>{t('yourAssessments')}</h4>
                                      {renderArtifacts(candidateData.artifacts)}
                                    </div>
                                  );
                                }
                                return null;
                              })}
                        </div>
                    </div>
                ))
            )}
         </main>

         {/* Right Sidebar */}
         <aside className="li-sidebar-right">
             <div className="li-card li-news-card">
                <div className="li-card-header">
                    <h3>Quick Actions</h3>
                </div>
                <div className="li-card-body">
                     <Link to="/agent-performance" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center', display: 'block', marginBottom: '10px', boxSizing: 'border-box' }}>
                        Agent Analytics
                     </Link>
                </div>
             </div>
             <div className="li-footer-links">
                 <Link to="/about">{t('footer.about')}</Link>
                 <Link to="/accessibility">{t('footer.accessibility')}</Link>
                 <Link to="/help-center">{t('footer.helpCenter')}</Link>
                 <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                 <span>{t('auth.footer.rights')}</span>
             </div>
         </aside>
      </div>
    </div>
  );
}

export default CandidateInterviews;